/**
 * Structured Logger com Correlation ID
 * Substituir todos console.log/error por este logger
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  correlationId?: string;
  userId?: string;
  sessionId?: string;
  [key: string]: any;
}

class Logger {
  private correlationId: string;
  private context: LogContext;

  constructor() {
    this.correlationId = this.generateCorrelationId();
    this.context = {};
  }

  private generateCorrelationId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public setCorrelationId(id: string) {
    this.correlationId = id;
  }

  public setContext(context: LogContext) {
    this.context = { ...this.context, ...context };
  }

  public getCorrelationId(): string {
    return this.correlationId;
  }

  private log(level: LogLevel, message: string, meta: any = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      correlationId: this.correlationId,
      ...this.context,
      ...meta
    };

    // Em produção, enviar para serviço de logging (Sentry, DataDog, etc)
    if (import.meta.env.PROD) {
      // TODO: Integrar com serviço de logging externo
      console.log(JSON.stringify(logEntry));
    } else {
      // Em dev, manter legível
      const emoji = {
        debug: '🔍',
        info: 'ℹ️',
        warn: '⚠️',
        error: '❌'
      }[level];
      
      console.log(
        `${emoji} [${level.toUpperCase()}] ${message}`,
        { correlationId: this.correlationId, ...meta }
      );
    }
  }

  public debug(message: string, meta?: any) {
    this.log('debug', message, meta);
  }

  public info(message: string, meta?: any) {
    this.log('info', message, meta);
  }

  public warn(message: string, meta?: any) {
    this.log('warn', message, meta);
  }

  public error(message: string, error?: Error | any, meta?: any) {
    this.log('error', message, {
      error: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error,
      ...meta
    });
  }

  // Helper para criar span de ação para debugging
  public startSpan(action: string, meta?: any) {
    const spanId = `${action}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.info(`[START] ${action}`, { ...meta, spanId });
    
    const startTime = Date.now();
    return {
      spanId,
      end: (status: 'success' | 'error', resultMeta?: any) => {
        const duration = Date.now() - startTime;
        if (status === 'success') {
          this.info(`[END] ${action}`, { ...meta, ...resultMeta, spanId, duration });
        } else {
          this.error(`[FAILED] ${action}`, undefined, { ...meta, ...resultMeta, spanId, duration });
        }
      }
    };
  }
}

// Singleton global
export const logger = new Logger();

// Helper para criar correlation ID em requests
export function getCorrelationHeaders() {
  return {
    'X-Correlation-ID': logger.getCorrelationId()
  };
}
